import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Upload, X, FileText, Image as ImageIcon, Loader2, CheckCircle2,
  Palette, Target, Trash2, Download, AlertCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 2 * 1024 * 1024;

export const PRIORITY_OPTIONS = [
  'Generate more enquiries',
  'Improve Google rankings',
  'Improve AI visibility',
  'Grow a particular practice area/service',
  'Target a specific city/region',
  'Increase brand awareness',
  'Competitor catch-up',
  'Something else',
];

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtSize = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

function FileChip({ file, onDelete, disabled }) {
  const isImg = (file.content_type || '').startsWith('image/');
  const href = `${API}/brand-priorities/files/${file.id}?auth=${localStorage.getItem('token')}`;
  return (
    <div className="flex items-center gap-3 p-2.5 rounded-xl bg-slate-50 border border-slate-200" data-testid={`brand-file-${file.id}`}>
      <div className="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
        {isImg ? <img src={href} alt={file.name} className="w-full h-full object-contain" /> : <FileText className="w-4 h-4 text-slate-500" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-slate-800 truncate">{file.name}</div>
        <div className="text-xs text-slate-400">{fmtSize(file.size || 0)}</div>
      </div>
      <a href={href} target="_blank" rel="noopener noreferrer" className="p-1.5 text-slate-400 hover:text-violet-600 transition" title="Download" data-testid={`brand-file-download-${file.id}`}>
        <Download className="w-4 h-4" />
      </a>
      {!disabled && onDelete && (
        <button type="button" onClick={() => onDelete(file.id)} className="p-1.5 text-slate-400 hover:text-red-500 transition" title="Remove" data-testid={`brand-file-delete-${file.id}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

/**
 * Brand & Priorities intake. Reused as a blocking modal (variant="modal") and
 * as a profile tab (variant="page"). readOnly hides all editing for viewers.
 */
export default function BrandPriorities({ firmId, variant = 'page', readOnly = false, onSubmitted, onSkip }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [form, setForm] = useState({
    brand_guidelines: '', brand_colours: '', reference_content: '', do_not_use: '',
    priorities: [], priorities_other: '', notes: '',
  });
  const [uploading, setUploading] = useState({ logo: false, asset: false });
  const logoInput = useRef(null);
  const assetInput = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/brand-priorities/${firmId}`, { headers: authHeader() });
      setData(res.data);
      setForm({
        brand_guidelines: res.data.brand_guidelines || '',
        brand_colours: res.data.brand_colours || '',
        reference_content: res.data.reference_content || '',
        do_not_use: res.data.do_not_use || '',
        priorities: res.data.priorities || [],
        priorities_other: res.data.priorities_other || '',
        notes: res.data.notes || '',
      });
    } catch (e) {
      setError('Could not load your Brand & Priorities.');
    } finally {
      setLoading(false);
    }
  }, [firmId]);

  useEffect(() => { load(); }, [load]);

  const togglePriority = (p) => {
    setForm((f) => ({
      ...f,
      priorities: f.priorities.includes(p) ? f.priorities.filter((x) => x !== p) : [...f.priorities, p],
    }));
  };

  const doUpload = async (file, kind) => {
    setError('');
    if (!file) return;
    if (file.size > MAX_BYTES) { setError(`"${file.name}" is over the 2MB limit.`); return; }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', kind);
    setUploading((u) => ({ ...u, [kind]: true }));
    try {
      await axios.post(`${API}/brand-priorities/${firmId}/files`, fd, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
      });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setUploading((u) => ({ ...u, [kind]: false }));
    }
  };

  const deleteFile = async (fileId) => {
    try {
      await axios.delete(`${API}/brand-priorities/${firmId}/files/${fileId}`, { headers: authHeader() });
      await load();
    } catch (e) { setError('Could not remove the file.'); }
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await axios.put(`${API}/brand-priorities/${firmId}`, { ...form, submit: true }, { headers: authHeader() });
      setData(res.data);
      if (onSubmitted) onSubmitted(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-400 p-6" data-testid="brand-priorities-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  const showOther = form.priorities.includes('Something else');
  const disabled = readOnly || saving;

  const Field = ({ label, hint, children }) => (
    <div>
      <label className="block text-sm font-semibold text-slate-800 mb-1">{label}</label>
      {hint && <p className="text-xs text-slate-400 mb-2">{hint}</p>}
      {children}
    </div>
  );

  const inputCls = 'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 transition disabled:bg-slate-50';

  return (
    <div className="space-y-8" data-testid="brand-priorities-form">
      {error && (
        <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-600" data-testid="brand-priorities-error">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {error}
        </div>
      )}

      {/* ── Brand & Assets ── */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center"><Palette className="w-4 h-4 text-violet-600" /></div>
          <h3 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Brand &amp; Assets</h3>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {/* Logo */}
          <Field label="Logo" hint="PNG, JPG or SVG · max 2MB">
            {data?.logo ? (
              <div className="space-y-2">
                <FileChip file={data.logo} onDelete={deleteFile} disabled={disabled} />
              </div>
            ) : (
              <button type="button" disabled={disabled || uploading.logo} onClick={() => logoInput.current?.click()}
                className="w-full flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 hover:border-violet-300 hover:bg-violet-50/40 px-4 py-4 text-sm text-slate-500 transition disabled:opacity-60" data-testid="brand-logo-upload-btn">
                {uploading.logo ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
                {uploading.logo ? 'Uploading…' : 'Upload logo'}
              </button>
            )}
            <input ref={logoInput} type="file" accept="image/*,.svg" className="hidden" data-testid="brand-logo-input"
              onChange={(e) => { doUpload(e.target.files?.[0], 'logo'); e.target.value = ''; }} />
          </Field>

          {/* Brand colours */}
          <Field label="Brand colours" hint="Type your hex codes or colour names">
            <input className={inputCls} disabled={disabled} placeholder="#5b21b6, Emerald green, Slate"
              value={form.brand_colours} onChange={(e) => setForm({ ...form, brand_colours: e.target.value })} data-testid="brand-colours-input" />
          </Field>
        </div>

        <div className="grid md:grid-cols-2 gap-5 mt-5">
          <Field label="Brand guidelines" hint="Fonts, tone of voice, style rules">
            <textarea rows={3} className={inputCls} disabled={disabled} placeholder="e.g. Use the Outfit font, professional but warm tone…"
              value={form.brand_guidelines} onChange={(e) => setForm({ ...form, brand_guidelines: e.target.value })} data-testid="brand-guidelines-input" />
          </Field>
          <Field label="Existing content to reference" hint="Pages, blogs or docs we should model">
            <textarea rows={3} className={inputCls} disabled={disabled} placeholder="Links or a short description of content you like…"
              value={form.reference_content} onChange={(e) => setForm({ ...form, reference_content: e.target.value })} data-testid="brand-reference-input" />
          </Field>
        </div>

        <div className="mt-5">
          <Field label={'“Do not use” wording / visuals'} hint="Anything we should avoid">
            <textarea rows={2} className={inputCls} disabled={disabled} placeholder="e.g. Never use Comic Sans, avoid the word ‘cheap’…"
              value={form.do_not_use} onChange={(e) => setForm({ ...form, do_not_use: e.target.value })} data-testid="brand-donotuse-input" />
          </Field>
        </div>

        {/* Additional files */}
        <div className="mt-5">
          <Field label="Upload files" hint="Brand guidelines PDF, assets, reference docs · max 2MB each">
            <div className="space-y-2">
              {(data?.files || []).map((f) => <FileChip key={f.id} file={f} onDelete={deleteFile} disabled={disabled} />)}
              {!readOnly && (
                <button type="button" disabled={uploading.asset} onClick={() => assetInput.current?.click()}
                  className="w-full flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 hover:border-violet-300 hover:bg-violet-50/40 px-4 py-3.5 text-sm text-slate-500 transition disabled:opacity-60" data-testid="brand-asset-upload-btn">
                  {uploading.asset ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading.asset ? 'Uploading…' : 'Add a file'}
                </button>
              )}
              {readOnly && (data?.files || []).length === 0 && <p className="text-sm text-slate-400">No files uploaded.</p>}
            </div>
            <input ref={assetInput} type="file" className="hidden" data-testid="brand-asset-input"
              accept="image/*,.pdf,.doc,.docx,.ppt,.pptx,.txt,.csv,.svg"
              onChange={(e) => { doUpload(e.target.files?.[0], 'asset'); e.target.value = ''; }} />
          </Field>
        </div>
      </section>

      {/* ── Top priorities ── */}
      <section>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center"><Target className="w-4 h-4 text-emerald-600" /></div>
          <h3 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Your top priorities</h3>
        </div>
        <p className="text-xs text-slate-400 mb-4 ml-10">Choose the outcomes that matter most to you right now.</p>

        <div className="grid sm:grid-cols-2 gap-2.5">
          {PRIORITY_OPTIONS.map((p) => {
            const on = form.priorities.includes(p);
            return (
              <button key={p} type="button" disabled={disabled} onClick={() => togglePriority(p)}
                className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm font-medium transition ${on ? 'border-violet-400 bg-violet-50 text-violet-800' : 'border-slate-200 bg-white text-slate-600 hover:border-violet-200 hover:bg-violet-50/40'} disabled:opacity-70`}
                data-testid={`priority-${p.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}>
                <span className={`w-4 h-4 rounded-md border flex items-center justify-center flex-shrink-0 ${on ? 'bg-violet-600 border-violet-600' : 'border-slate-300'}`}>
                  {on && <CheckCircle2 className="w-3 h-3 text-white" />}
                </span>
                {p}
              </button>
            );
          })}
        </div>

        {showOther && (
          <div className="mt-3">
            <input className={inputCls} disabled={disabled} placeholder="Tell us more about your ‘something else’…"
              value={form.priorities_other} onChange={(e) => setForm({ ...form, priorities_other: e.target.value })} data-testid="priority-other-input" />
          </div>
        )}

        <div className="mt-5">
          <Field label="Anything else your account manager should know?" hint="Optional">
            <textarea rows={2} className={inputCls} disabled={disabled} placeholder="Share any extra context here…"
              value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="brand-notes-input" />
          </Field>
        </div>
      </section>

      {/* Actions */}
      {!readOnly && (
        <div className={`flex items-center gap-3 ${variant === 'modal' ? 'justify-between' : 'justify-end'} pt-2`}>
          {variant === 'modal' && onSkip && (
            <button type="button" onClick={onSkip} className="text-sm text-slate-500 hover:text-slate-700 underline underline-offset-2 transition" data-testid="brand-priorities-skip">
              I’ll do this later
            </button>
          )}
          <button type="button" onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-violet-600/30 transition disabled:opacity-50"
            data-testid="brand-priorities-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {data?.submitted ? 'Save changes' : 'Submit'}
          </button>
        </div>
      )}
    </div>
  );
}
